/** UN NIVEL AL SOLUȚIEI ARHITECTURALE, deschis în editorul de tronsoane.
 *
 *  Cerut de Stefan (23.09.2026): planul soluției să se editeze «în același mod»
 *  ca un tronson — tras de obiecte, proprietăți la clic, salvat în soluție — și
 *  «când modificăm codul pentru tronsoane să se modifice și în arhitectură, și
 *  invers». De-aia nivelul NU are editorul lui: se împachetează ca document de
 *  tronson, se editează pe aceeași masă (`tronson/editor/Masa`), cu aceleași
 *  funcții de model, și se despachetează înapoi în obiecte canonice. Tot ce
 *  învață editorul de tronsoane învață și nivelul, fără o linie scrisă aici.
 *
 *  CUM SE ÎMPACHETEAZĂ.
 *
 *    * Un tronson IZOLAT (`role: "standalone"`), cu front rigid: cadrul
 *      colțului, totul pe latura A, fără axă de coridor inventată (v. antetul
 *      din `tronson/doc.ts`). Fiecare coordonată e `{a}`, fără `k` — un nivel
 *      nu se întinde.
 *    * Conturul e poligonul `Storey`, scris ca `plan.outline`: e anvelopa în
 *      care se derivă camerele, și urmează zidurile ca pe un tronson.
 *    * ÎN CADRUL CLĂDIRII, nu al desenului. Soluțiile generate stau pe lot la
 *      unghiul corpului (pereți la 176° și 266° pe «test 5»), iar editorul
 *      lucrează în unghiuri drepte: mută pe x sau pe y, lipește pe orizontală
 *      și pe verticală. Deci fiecare clădire se rotește cu unghiul ei
 *      dominant, O DATĂ, același pentru toate nivelurile ei — altfel un zid
 *      identic pe etajul 2 și pe etajul 3 n-ar mai fi identic și propagarea
 *      n-ar avea ce recunoaște.
 *    * Fiecare obiect își ține `eid`-ul (pereții în `id`). La despachetare, un
 *      obiect cu `eid` se rescrie PE LOC, cu tot ce avea în document și
 *      editorul nu cunoaște (`props` de structură, `group`, `source`); unul
 *      fără se naște; unul care a dispărut se șterge. Obiectele pe care
 *      editorul nu le deschide (stâlpi, grinzi, plăci, fațadă, uși fără
 *      poziție) nu sunt atinse deloc.
 *
 *  MOBILA, PRIZELE, FINISAJELE. Modelul canonic n-are un tip pentru ele, iar
 *  soluțiile generate nu le au. Se scriu pe `Storey`, în
 *  `props["architecture.amenajare"]`, cu pozițiile ca PUNCTE și orientarea ca
 *  VERSORI — ca să treacă prin aceleași transformări de cadru ca orice punct
 *  din `props` (v. `canonical.PROPS_WORLD_POINTS`). Un nivel fără ele se
 *  deschide fără ele: mobila dedusă din felul camerei NU se scrie la deschidere
 *  (Stefan: «dacă mobila nu e desenată în soluție nu o desenăm»).
 *
 *  PROPAGAREA (`propaga`). O schimbare făcută pe un nivel se duce pe celelalte
 *  niveluri ale clădirii acolo unde ele sunt LA FEL: «toate nivelurile
 *  identice și porțiunea comună de pe nivelurile puțin diferite» — etajul
 *  retras are o parte comună și una a lui. «La fel» se măsoară pe geometrie,
 *  la un centimetru, nu pe `group`: măsurat pe două soluții reale, `group`
 *  leagă obiecte din clădiri diferite, cu geometrie diferită, iar geometria
 *  recunoaște exact porțiunea comună (P+4 importat: etajele 1–2 100 %, etajul
 *  3 42 %, parterul 22 %).
 */
import type { CanonicalDoc, CanonicalObject } from "../canonical";
import { laOrigineLocala, northDown, ownerOf, storeysOf } from "../canonical";
import type {
  CameraDoc, ElectricDoc, FinisajDoc, GolDoc, PereteDoc, PiesaDoc, Pt,
  StratDoc, TronsonDoc, VarfAfin,
} from "../tronson/doc";
import { inInel, pePeretele } from "../tronson/doc";
import { axaPeretelui, conturulDupaDesen, cuAxeScrise }
  from "../tronson/editor/model";
import { cuCamereDerivate, reperulCamerelor } from "../tronson/editor/camere";

// --------------------------------------------------------------- cadrul ---

/** Cadrul unei clădiri: `local = R(−teta) · (p − o)`, în cadrul desenului.
 *
 *  `oglindit` spune dacă desenul e o oglindire a documentului (`northUp` pe o
 *  grilă obișnuită) și nu un sfert de tură: la oglindire, lucrurile care au o
 *  MÂNĂ — sensul unei uși, ordinea straturilor de la stânga la dreapta — se
 *  întorc, deci se întorc și la scriere. */
export type Cadru = { o: Pt; teta: number; oglindit: boolean };

const r3 = (v: number) => Math.round(v * 1000) / 1000;
/** Pe masă, coordonatele la MICROMETRU, nu la milimetru: rotirea lasă zgomot
 *  de virgulă pe care îl tai, dar o rotunjire la milimetru ar fi mutat cu până
 *  la jumătate de milimetru fiecare vârf NEATINS al unui zid rescris — o
 *  pierdere de geometrie, nu o curățenie (documentele importate au zecimea de
 *  milimetru). Gesturile mesei rotunjesc oricum la milimetru ce mută. */
const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
/** La scriere, zecimea de milimetru: precizia documentelor importate. */
const r4 = (v: number) => Math.round(v * 1e4) / 1e4;
const rot = (p: readonly number[], t: number): Pt =>
  [p[0] * Math.cos(t) - p[1] * Math.sin(t), p[0] * Math.sin(t) + p[1] * Math.cos(t)];

export function laLocal(c: Cadru, p: readonly number[]): Pt {
  const q = rot([p[0] - c.o[0], p[1] - c.o[1]], -c.teta);
  return [r6(q[0]), r6(q[1])];
}

export function dinLocal(c: Cadru, p: readonly number[]): number[] {
  const q = rot(p, c.teta);
  return [r6(q[0] + c.o[0]), r6(q[1] + c.o[1])];
}

const GRADE = 180 / Math.PI;
const degLocal = (c: Cadru, deg: number) => unghi(deg - c.teta * GRADE);
const degDin = (c: Cadru, deg: number) => unghi(deg + c.teta * GRADE);
/** Un unghi în [0, 360), rotunjit la miime de grad — rotunjit ÎNAINTE de
 *  plafon, altfel 359,9999 iese 360. */
function unghi(a: number): number {
  const x = r3(((a % 360) + 360) % 360);
  return x >= 360 ? 0 : x;
}

const g = (o: CanonicalObject, k: string): any => o.geometry?.[k];

/** Obiectele unui nivel, pe tip. */
function peNivel(doc: CanonicalDoc, storeyId: string): Map<string, CanonicalObject[]> {
  const out = new Map<string, CanonicalObject[]>();
  for (const o of doc.objects ?? []) {
    if (o.properties?.storey !== storeyId) continue;
    const l = out.get(o.type);
    if (l) l.push(o); else out.set(o.type, [o]);
  }
  return out;
}

/** Inelul DESCHIS al unui obiect: fără vârful de închidere și fără vârfurile
 *  repetate. Pereții generați vin cu vârfuri dublate la îmbinările în T (opt
 *  vârfuri, două perechi identice), iar editorul le-ar fi arătat ca mânere
 *  suprapuse. */
function inelDeschis(poly: readonly number[][] | null | undefined): number[][] {
  if (!poly?.length) return [];
  const out: number[][] = [];
  for (const p of poly) {
    const q = out[out.length - 1];
    if (q && Math.abs(q[0] - p[0]) < 1e-6 && Math.abs(q[1] - p[1]) < 1e-6) continue;
    out.push(p);
  }
  const a = out[0], z = out[out.length - 1];
  if (out.length > 1 && Math.abs(a[0] - z[0]) < 1e-6 && Math.abs(a[1] - z[1]) < 1e-6) {
    out.pop();
  }
  return out;
}

function inelDinAxa(axis: number[][], t: number): number[][] {
  const [[x0, y0], [x1, y1]] = [axis[0], axis[axis.length - 1]];
  const L = Math.hypot(x1 - x0, y1 - y0);
  if (!(L > 0) || !(t > 0)) return [];
  const nx = (-(y1 - y0) / L) * (t / 2), ny = ((x1 - x0) / L) * (t / 2);
  return [[x0 + nx, y0 + ny], [x1 + nx, y1 + ny], [x1 - nx, y1 - ny], [x0 - nx, y0 - ny]];
}

function inelDinCentru(o: CanonicalObject): number[][] {
  const [cx, cy, w, h] = [g(o, "cx"), g(o, "cy"), g(o, "w"), g(o, "h")];
  if (![cx, cy, w, h].every((v) => typeof v === "number") || !(w > 0 && h > 0)) return [];
  const t = ((g(o, "deg") ?? 0) * Math.PI) / 180;
  const pt = (u: number, v: number) =>
    [cx + u * Math.cos(t) - v * Math.sin(t), cy + u * Math.sin(t) + v * Math.cos(t)];
  return [pt(-w / 2, -h / 2), pt(w / 2, -h / 2), pt(w / 2, h / 2), pt(-w / 2, h / 2)];
}

/** Cadrul clădirii care deține nivelul — unghiul dominant al pereților tuturor
 *  nivelurilor ei, iar originea în colțul cutiei lor. */
export function cadrulCladirii(doc: CanonicalDoc, storeyId: string,
                               oglindit: boolean): Cadru {
  return cadrulCladirilor(doc, [storeyId], oglindit);
}

/** CADRUL COMUN AL MAI MULTOR CLĂDIRI — ale nivelurilor date, cu toate
 *  nivelurile lor. E cadrul mesei pe «Toate corpurile»: corpurile unei cote
 *  stau pe aceeași pânză, deci în aceleași coordonate, iar fiecare își
 *  păstrează același cadru pe toate nivelurile lui (v. `propaga`).
 *
 *  Unghiul e votat pe pereții TUTUROR clădirilor. Pe planurile de situație
 *  generate corpurile sunt paralele — măsurat pe două soluții reale: −9,71°
 *  pe toate trei corpurile din «test», −8,70° pe toate patru din celălalt —,
 *  deci votul e chiar unghiul fiecăruia. Un corp rotit altfel ar ieși cu
 *  pereții oblici pe masă. */
export function cadrulCladirilor(doc: CanonicalDoc, storeyIds: readonly string[],
                                 oglindit: boolean): Cadru {
  const owners = new Set<string | undefined>();
  const ale = new Set<string>();
  for (const storeyId of storeyIds) {
    const st = (doc.objects ?? []).find((o) => o.id === storeyId);
    const owner = st ? ownerOf(st) : undefined;
    owners.add(owner);
    const lui = storeysOf(doc, owner).map((s) => s.id);
    for (const id of lui) ale.add(id);
    if (!lui.length) ale.add(storeyId);
  }
  // Unghiul dominant, modulo 90°: media vectorilor (cos 4a, sin 4a) ponderați
  // cu lungimea — un zid la 176° și unul la 86° votează la fel.
  let sx = 0, sy = 0;
  const puncte: number[][] = [];
  for (const o of doc.objects ?? []) {
    if (!ale.has(o.properties?.storey as string)) continue;
    if (o.type === "Storey") continue;
    if (o.type === "Wall") {
      const ax = g(o, "axis") as number[][] | undefined;
      if (ax && ax.length >= 2) {
        const dx = ax[ax.length - 1][0] - ax[0][0], dy = ax[ax.length - 1][1] - ax[0][1];
        const L = Math.hypot(dx, dy);
        const a = Math.atan2(dy, dx);
        sx += L * Math.cos(4 * a);
        sy += L * Math.sin(4 * a);
      }
    }
  }
  // ORIGINEA DIN AMPRENTA CLĂDIRII, pe care editorul n-o scrie niciodată — nu
  // din conturul nivelurilor, care se rescrie (contur tras, mobilă pusă). O
  // origine luată de pe un poligon rescris sărea cu un metru întreg la
  // redeschidere: dus-întorsul prin cadre lasă `−0,0000001` unde era `0`, iar
  // rotunjirea în jos de dedesubt îl ducea la −1.
  for (const owner of owners) {
    const cladire = owner ? (doc.objects ?? []).find((o) => o.id === owner) : undefined;
    for (const p of inelDeschis(cladire ? g(cladire, "poly") : null)) puncte.push(p);
  }
  if (!puncte.length) {
    for (const id of ale) {
      const s = (doc.objects ?? []).find((o) => o.id === id);
      for (const p of inelDeschis(s ? g(s, "poly") : null)) puncte.push(p);
    }
  }
  if (!puncte.length) {
    for (const o of doc.objects ?? []) {
      if (!ale.has(o.properties?.storey as string) || o.type !== "Wall") continue;
      for (const p of inelDeschis(g(o, "poly") ?? g(o, "axis"))) puncte.push(p);
    }
  }
  let teta = sx || sy ? Math.atan2(sy, sx) / 4 : 0;
  // Un unghi sub o zecime de grad e zgomotul transcrierii, nu o rotire a
  // corpului: pe P+4 importat axele stau la 89,9993°. Lăsat, fiecare
  // coordonată ar fi ieșit cu o virgulă în plus fără niciun câștig.
  if (Math.abs(teta) < 0.1 / GRADE) teta = 0;
  let mx = Infinity, my = Infinity;
  for (const p of puncte) {
    const q = rot(p, -teta);
    mx = Math.min(mx, q[0]);
    my = Math.min(my, q[1]);
  }
  if (!Number.isFinite(mx)) { mx = 0; my = 0; }
  // Colțul, rotunjit la metru în cadrul rotit: numere mici, iar un colț de
  // placă mutat cu doi centimetri nu mută tot cadrul.
  const o = rot([Math.floor(mx + 1e-6), Math.floor(my + 1e-6)], teta);
  return { o: [o[0], o[1]], teta, oglindit };
}

// ----------------------------------------------------------- împachetare ---

/** Ce se ține lângă documentul de tronson ca să se poată despacheta. */
export type NivelDeschis = {
  storeyId: string;
  owner?: string;
  cadru: Cadru;
  /** Documentul cum s-a deschis — față de el se măsoară ce s-a schimbat. */
  initial: TronsonDoc;
  /** Obiectele canonice deschise, pe id: ce nu știe editorul se ia de aici. */
  obiecte: Map<string, CanonicalObject>;
  /** Apartamentele nivelului, în ordinea din `CameraDoc.flat`. */
  flats: string[];
  /** Nucleul deschis, dacă nivelul are unul. */
  core?: string;
};

const va = (p: readonly number[]): VarfAfin => [{ a: p[0] }, { a: p[1] }];
const inelLocal = (c: Cadru, poly: readonly number[][]): VarfAfin[] =>
  poly.map((p) => va(laLocal(c, p)));

/** Materialul unui zid care nu-l spune: cel al felului lui. */
const BETON = new Set(["structural", "facade", "fire", "party"]);

type AmenajareScrisa = {
  piese?: { kind: string; w_m?: number; h_m?: number; flip_x?: boolean;
            flip_y?: boolean; z_m?: number }[];
  piese_c?: number[][];
  piese_dir?: number[][];
  aparate?: { kind: string; h_m?: number; circuit?: string; pentru?: number;
              parte?: "stanga" | "dreapta" | "spate"; flip_x?: boolean;
              flip_y?: boolean; devices?: string[] }[];
  aparate_c?: number[][];
  aparate_dir?: number[][];
  finisaje?: { space: string; floor?: string; wall?: string }[];
};
/** Cheia sub care stă amenajarea pe `Storey`. */
export const AMENAJARE = "architecture.amenajare";

const unghiDinDir = (d: number[] | undefined) =>
  d ? Math.atan2(d[1], d[0]) * GRADE : 0;
const dirDinUnghi = (deg: number) => {
  const r = deg / GRADE;
  return [r6(Math.cos(r)), r6(Math.sin(r))];
};

/** Nivelul `storeyId` al documentului (în cadrul DESENULUI — `northUp` și
 *  originea locală aplicate), ca document de tronson. */
export function nivelLaTronson(doc: CanonicalDoc, storeyId: string,
                               cadru: Cadru): NivelDeschis {
  const st = (doc.objects ?? []).find((o) => o.id === storeyId);
  if (!st) throw new Error(`nivelul ${storeyId} nu e în document`);
  const owner = ownerOf(st);
  const on = peNivel(doc, storeyId);
  const obiecte = new Map<string, CanonicalObject>([[st.id, st]]);

  const walls: PereteDoc[] = [];
  for (const o of on.get("Wall") ?? []) {
    const t = g(o, "thickness_m") as number | null | undefined;
    const ax = g(o, "axis") as number[][] | undefined;
    const bulge = g(o, "bulge") as number | null | undefined;
    let ring = inelDeschis(g(o, "poly"));
    if (ring.length < 3 && ax && ax.length >= 2) ring = inelDinAxa(ax, t ?? 0.2);
    if (ring.length < 3) continue;
    obiecte.set(o.id, o);
    const aw = (o.props?.["architecture.wall"] ?? {}) as {
      material?: string; material_presupus?: boolean; on_facade?: boolean;
      encloses_core?: boolean; layers?: StratDoc[] };
    const kind = (o.properties.kind ?? "partition") as string;
    const layers = aw.layers?.length
      ? (cadru.oglindit ? [...aw.layers].reverse() : aw.layers) : undefined;
    walls.push({
      id: o.id,
      kind,
      material: aw.material ?? (BETON.has(kind) ? "beton" : "zidarie"),
      thickness_m: t ?? 0.2,
      height_m: (g(o, "height_m") as number | null | undefined) ?? null,
      ...(ax && ax.length >= 2
        ? { axis: [laLocal(cadru, ax[0]), laLocal(cadru, ax[ax.length - 1])] as [Pt, Pt] }
        : {}),
      // `bulge` stă în `geometry` și e definit pe COORDONATE (sensul în care
      // crește unghiul de la axis[0] la axis[1]), deci trece prin rotirea
      // cadrului neschimbat; `oglindit` e al desenului, nu al coordonatelor
      ...(bulge ? { bulge } : {}),
      ...(layers ? { layers } : {}),
      ...(aw.material_presupus ? { material_presupus: true } : {}),
      ...(aw.on_facade ? { on_facade: true } : {}),
      ...(aw.encloses_core ? { encloses_core: true } : {}),
      poly: inelLocal(cadru, ring),
    });
  }

  const flats = (on.get("Flat") ?? []).map((f) => f.id);
  const rooms: CameraDoc[] = [];
  for (const o of on.get("Space") ?? []) {
    let ring = inelDeschis(g(o, "poly"));
    if (ring.length < 3) ring = inelDinCentru(o);
    if (ring.length < 3) continue;
    obiecte.set(o.id, o);
    const afara = (o.props?.["architecture.space"] as { outdoor?: string } | undefined)?.outdoor;
    const fi = flats.indexOf(o.properties.flat as string);
    const label = o.display?.label as string | undefined;
    rooms.push({
      eid: o.id,
      kind: (o.properties.kind ?? "room") as string,
      ...(label ? { label } : {}),
      side: "A",
      ...(fi >= 0 ? { flat: fi } : {}),
      ...(afara === "balcony" || afara === "loggia" ? { outdoor_kind: afara } : {}),
      poly: inelLocal(cadru, ring),
    });
  }

  const gol = (o: CanonicalObject, usa: boolean): GolDoc | null => {
    const cx = g(o, "cx"), cy = g(o, "cy");
    if (typeof cx !== "number" || typeof cy !== "number") return null;
    obiecte.set(o.id, o);
    const op = (o.props?.["architecture.opening"] ?? {}) as {
      model?: GolDoc["model"]; hinge?: GolDoc["hinge"]; frame_m?: number;
      swing?: 1 | -1; origin?: GolDoc["origin"] };
    const [x, y] = laLocal(cadru, [cx, cy]);
    const swing = op.swing ? ((cadru.oglindit ? -op.swing : op.swing) as 1 | -1) : undefined;
    return {
      eid: o.id,
      x: { a: x }, y: { a: y },
      w_m: g(o, "width_m") ?? 0.9,
      rot_deg: degLocal(cadru, g(o, "deg") ?? 0),
      wall: (o.properties.wall as string | null | undefined) ?? null,
      u_m: g(o, "u_m") ?? null,
      sill_m: g(o, "sill_m") ?? null,
      height_m: g(o, "height_m") ?? null,
      ...(usa ? {
        kind: o.properties.role === "entry" ? "entrance"
          : o.properties.kind === "fire_door" ? "fire" : "room",
      } as Partial<GolDoc> : {}),
      ...(op.model ? { model: op.model } : {}),
      ...(op.hinge ? { hinge: op.hinge } : {}),
      ...(op.frame_m != null ? { frame_m: op.frame_m } : {}),
      ...(swing ? { swing } : {}),
      ...(op.origin ? { origin: op.origin } : {}),
    };
  };
  const doors = (on.get("Door") ?? []).map((o) => gol(o, true))
    .filter((x): x is GolDoc => x != null);
  const windows = (on.get("Window") ?? []).map((o) => gol(o, false))
    .filter((x): x is GolDoc => x != null);

  // NUCLEUL: editorul știe unul singur, dreptunghiular. Pe un nivel cu mai
  // multe se deschide primul; celelalte rămân în document, neatinse.
  let core: string | undefined;
  let core_rect: number[] | undefined;
  let core_parts: { kind: string; poly: number[][]; eid?: string }[] | undefined;
  const nucleu = (on.get("Core") ?? []).find((o) => inelDeschis(g(o, "poly")).length >= 3);
  if (nucleu) {
    const ring = inelDeschis(g(nucleu, "poly")).map((p) => laLocal(cadru, p));
    const xs = ring.map((p) => p[0]), ys = ring.map((p) => p[1]);
    core = nucleu.id;
    obiecte.set(nucleu.id, nucleu);
    core_rect = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    core_parts = (on.get("CorePart") ?? [])
      .filter((p) => p.properties.core === nucleu.id)
      .map((p) => {
        let r = inelDeschis(g(p, "poly"));
        if (r.length < 3) r = inelDinCentru(p);
        obiecte.set(p.id, p);
        return { kind: (p.properties.kind ?? "scara") as string,
                 poly: r.map((q) => laLocal(cadru, q)), eid: p.id };
      })
      .filter((p) => p.poly.length >= 3);
  }

  // Conturul: al nivelului, altfel al clădirii care îl deține.
  let contur = inelDeschis(g(st, "poly"));
  if (contur.length < 3 && owner) {
    const o = (doc.objects ?? []).find((x) => x.id === owner);
    contur = inelDeschis(o ? g(o, "poly") : null);
  }
  const outline = contur.map((p) => laLocal(cadru, p));
  const xs = outline.map((p) => p[0]), ys = outline.map((p) => p[1]);
  const lat = outline.length ? r3(Math.max(...xs) - Math.min(...xs)) : 10;
  const adanc = outline.length ? r3(Math.max(...ys) - Math.min(...ys)) : 10;

  const am = (st.props?.[AMENAJARE] ?? {}) as AmenajareScrisa;
  const furniture: PiesaDoc[] = (am.piese ?? []).map((p, i) => {
    const [x, y] = laLocal(cadru, am.piese_c?.[i] ?? [0, 0]);
    return { ...p, x: { a: x }, y: { a: y },
             rot_deg: degLocal(cadru, unghiDinDir(am.piese_dir?.[i])) };
  });
  const electrical: ElectricDoc[] = (am.aparate ?? []).map((p, i) => {
    const [x, y] = laLocal(cadru, am.aparate_c?.[i] ?? [0, 0]);
    return { ...p, x: { a: x }, y: { a: y },
             rot_deg: degLocal(cadru, unghiDinDir(am.aparate_dir?.[i])) };
  });
  const finishes: FinisajDoc[] = (am.finisaje ?? [])
    .map((f) => ({ room: rooms.findIndex((r) => r.eid === f.space),
                   ...(f.floor ? { floor: f.floor } : {}),
                   ...(f.wall ? { wall: f.wall } : {}) }))
    .filter((f) => f.room >= 0);

  const nume = (st.properties.name ?? st.display?.label ?? storeyId) as string;
  const cod = owner
    ? ((doc.objects ?? []).find((o) => o.id === owner)?.properties.code ?? "") as string
    : "";
  const tronson: TronsonDoc = {
    schema_version: "1.0",
    id: nume,
    ...(cod ? { label: cod } : {}),
    kind: "nivel",
    role: "standalone",
    band_depth_m: adanc,
    ...(typeof g(st, "height_m") === "number" ? { storey_height_m: g(st, "height_m") } : {}),
    front: { nominal_m: lat, min_m: lat, max_m: lat, rigid: true },
    sides: { A: { flats: flats.map(() => ({ camere: 0, rooms: {} })) } },
    variants: [{ id: "nivel", outdoor: [], useful_m2: [], default: true }],
    plan: {
      rooms, walls, doors, windows,
      ...(outline.length >= 3 ? { outline } : {}),
      ...(furniture.length ? { furniture } : {}),
      ...(electrical.length ? { electrical } : {}),
      ...(finishes.length ? { finishes } : {}),
      ...(core_rect ? { core_rect, core_parts } : {}),
    },
  };
  return { storeyId, owner, cadru, initial: cuAxeScrise(tronson), obiecte, flats, core };
}

// --------------------------------------------------------- despachetare ---

/** Ce se scrie în soluție: obiecte întregi (înlocuite sau noi) și id-uri de
 *  șters. În cadrul DESENULUI — pagina le întoarce în cel al documentului. */
export type Scriere = { obiecte: CanonicalObject[]; sterge: string[] };

const HEX = "0123456789abcdef";
/** Un id nou, cu prefixul tipului, în forma celor emise (16 cifre hex). */
export function idCanonic(prefix: string): string {
  let s = "";
  const c = globalThis.crypto;
  if (c?.getRandomValues) {
    const b = c.getRandomValues(new Uint8Array(8));
    for (const x of b) s += HEX[x >> 4] + HEX[x & 15];
  } else {
    for (let i = 0; i < 16; i++) s += HEX[Math.floor(Math.random() * 16)];
  }
  return `${prefix}_${s}`;
}

const inelVal = (poly: readonly VarfAfin[]): Pt[] => poly.map((v) => [v[0].a, v[1].a]);
const acelasi = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const SURSA = { kind: "manual" as const, engine: "editor" };

/** Ce trebuie scris ca nivelul din soluție să fie `final`. Doar ce s-a schimbat
 *  față de `n.initial`. */
export function tronsonLaObiecte(n: NivelDeschis, final: TronsonDoc): Scriere {
  const c = n.cadru;
  const init = n.initial.plan!;
  const plan = final.plan!;
  const obiecte: CanonicalObject[] = [];
  const sterge: string[] = [];
  const st = n.obiecte.get(n.storeyId)!;
  const baza = { storey: n.storeyId, ...(n.owner ? { building: n.owner } : {}) };
  const din = (p: readonly number[]) => dinLocal(c, p);

  // ---- pereții
  const peretiInit = new Map((init.walls ?? []).map((w) => [w.id, w]));
  const idPerete = new Map<string, string>();  // id din editor → id canonic
  const axaFinala = new Map<string, { axa: number[][]; t: number; bulge: number }>();
  const vazuti = new Set<string>();
  for (const w of plan.walls ?? []) {
    const orig = peretiInit.has(w.id) && !vazuti.has(w.id) ? n.obiecte.get(w.id) : undefined;
    const id = orig ? w.id : idCanonic("wl");
    vazuti.add(w.id);
    idPerete.set(w.id, id);
    const axa = axaPeretelui(w).map(din);
    axaFinala.set(id, { axa, t: w.thickness_m, bulge: w.bulge ?? 0 });
    if (orig && acelasi(peretiInit.get(w.id), w)) continue;
    const aw = { ...((orig?.props?.["architecture.wall"] ?? {}) as Record<string, unknown>) };
    aw.material = w.material;
    // presupunerea importului pleacă odată cu alegerea omului (v. editorul)
    if (w.material_presupus) aw.material_presupus = true;
    else delete aw.material_presupus;
    aw.on_facade = !!w.on_facade;
    aw.encloses_core = !!w.encloses_core;
    if (w.layers?.length) aw.layers = c.oglindit ? [...w.layers].reverse() : w.layers;
    else delete aw.layers;
    // curbura e GEOMETRIE (`geometry.bulge`, schema 3.2), nu o proprietate a
    // arhitecturii: o copie rămasă aici ar fi o a doua sursă pentru aceeași formă
    delete aw.bulge;
    obiecte.push({
      ...(orig ?? { id, type: "Wall", source: SURSA }),
      id,
      type: "Wall",
      properties: { ...(orig?.properties ?? baza), kind: w.kind },
      geometry: {
        ...(orig?.geometry ?? {}),
        poly: inelVal(w.poly).map(din),
        axis: axa,
        bulge: w.bulge ? w.bulge : undefined,
        thickness_m: w.thickness_m,
        ...(w.height_m != null ? { height_m: w.height_m }
          : orig?.geometry?.height_m !== undefined ? { height_m: null } : {}),
      },
      props: { ...(orig?.props ?? {}), "architecture.wall": aw },
    } as CanonicalObject);
  }
  for (const id of peretiInit.keys()) if (!vazuti.has(id)) sterge.push(id);

  // ---- golurile
  const golInit = new Map<string, GolDoc>();
  for (const x of [...(init.doors ?? []), ...(init.windows ?? [])]) {
    if (x.eid) golInit.set(x.eid, x);
  }
  const goluriVazute = new Set<string>();
  const scrieGolurile = (lista: readonly GolDoc[], usa: boolean) => {
    for (const x of lista) {
      const orig = x.eid && golInit.has(x.eid) && !goluriVazute.has(x.eid)
        ? n.obiecte.get(x.eid) : undefined;
      if (x.eid) goluriVazute.add(x.eid);
      const gazda = x.wall ? (idPerete.get(x.wall) ?? x.wall) : null;
      const gazdaInit = orig ? (golInit.get(x.eid!)!.wall ?? null) : undefined;
      // Un gol neatins, al cărui zid n-a fost nici el atins, rămâne cum e.
      // Dacă zidul s-a mutat, golul se rescrie oricum: `u_m` e măsurat pe
      // axa lui, deci s-a schimbat chiar dacă desenul golului nu.
      if (orig && acelasi(golInit.get(x.eid!), x) && gazda === gazdaInit
          && !obiecte.some((o) => o.id === gazda)) continue;
      const id = orig ? x.eid! : idCanonic(usa ? "dr" : "wn");
      const [cx, cy] = din([x.x.a, x.y.a]);
      const deg = degDin(c, x.rot_deg ?? 0);
      const ax = gazda ? axaFinala.get(gazda) : undefined;
      let u = x.u_m ?? null;
      if (ax) {
        // de-a lungul axului până la CENTRUL golului — pe un zid curb, pe arc
        const [a, b] = [ax.axa[0], ax.axa[ax.axa.length - 1]] as [Pt, Pt];
        u = r3(pePeretele(a, b, ax.bulge, [cx, cy]).u);
      }
      const geo: Record<string, unknown> = {
        ...(orig?.geometry ?? {}),
        cx, cy, deg, width_m: x.w_m ?? 0.9, u_m: u,
        sill_m: x.sill_m ?? null, height_m: x.height_m ?? null,
      };
      // Golul importat poartă și inelul lui: se reface pe noua poziție, altfel
      // desenul l-ar lua pe cel vechi (inelul bate calea parametrică).
      if (orig?.geometry?.poly) {
        const t = (orig.geometry.thickness_m as number | undefined) ?? ax?.t ?? 0.2;
        const r = deg / GRADE;
        const [ux, uy] = [Math.cos(r), Math.sin(r)];
        const w2 = (x.w_m ?? 0.9) / 2, t2 = t / 2;
        geo.poly = [[-w2, -t2], [w2, -t2], [w2, t2], [-w2, t2]]
          .map(([p, q]) => [r6(cx + p * ux - q * uy), r6(cy + p * uy + q * ux)]);
      }
      const op = { ...((orig?.props?.["architecture.opening"] ?? {}) as Record<string, unknown>) };
      if (x.model) op.model = x.model; else if (usa) delete op.model;
      if (x.hinge) op.hinge = x.hinge;
      if (x.frame_m != null) op.frame_m = x.frame_m;
      if (x.swing) op.swing = c.oglindit ? -x.swing : x.swing; else delete op.swing;
      const props: Record<string, unknown> = {
        ...(baza), ...(orig?.properties ?? {}), wall: gazda,
      };
      if (usa) {
        props.kind = x.kind === "fire" ? "fire_door" : "door";
        props.role = x.kind === "entrance" ? "entry" : "interior";
        if (!("serves" in props)) props.serves = null;
      }
      obiecte.push({
        ...(orig ?? { id, type: usa ? "Door" : "Window", source: SURSA }),
        id,
        type: usa ? "Door" : "Window",
        properties: props,
        geometry: geo,
        props: { ...(orig?.props ?? {}), "architecture.opening": op },
      } as CanonicalObject);
    }
  };
  scrieGolurile(plan.doors ?? [], true);
  scrieGolurile(plan.windows ?? [], false);
  for (const id of golInit.keys()) if (!goluriVazute.has(id)) sterge.push(id);

  // ---- camerele
  const camInit = new Map<string, CameraDoc>();
  for (const r of init.rooms ?? []) if (r.eid) camInit.set(r.eid, r);
  const camVazute = new Set<string>();
  const idCamera: string[] = [];
  for (const r of plan.rooms ?? []) {
    const orig = r.eid && camInit.has(r.eid) && !camVazute.has(r.eid)
      ? n.obiecte.get(r.eid) : undefined;
    if (r.eid) camVazute.add(r.eid);
    const id = orig ? r.eid! : idCanonic("rm");
    idCamera.push(id);
    if (orig && acelasi(camInit.get(r.eid!)!.poly, r.poly)
        && camInit.get(r.eid!)!.kind === r.kind
        && camInit.get(r.eid!)!.label === r.label
        && camInit.get(r.eid!)!.outdoor_kind === r.outdoor_kind
        && camInit.get(r.eid!)!.flat === r.flat) continue;
    const geo: Record<string, unknown> = { ...(orig?.geometry ?? {}),
                                           poly: inelVal(r.poly).map(din) };
    // Camera scrisă ca dreptunghi rotit (`cx/cy/w/h/deg`) are de-acum inel;
    // dreptunghiul vechi n-ar mai spune adevărul despre ea.
    if (!acelasi(camInit.get(r.eid ?? "")?.poly, r.poly)) {
      for (const k of ["cx", "cy", "w", "h", "deg"]) delete geo[k];
    }
    const flat = r.flat != null ? n.flats[r.flat] : undefined;
    const props: Record<string, unknown> = { ...(orig?.properties ?? baza), kind: r.kind };
    if (flat) props.flat = flat;
    const display: Record<string, unknown> = { ...(orig?.display ?? {}) };
    if (r.label) display.label = r.label; else delete display.label;
    const as = { ...((orig?.props?.["architecture.space"] ?? {}) as Record<string, unknown>) };
    if (r.outdoor_kind) as.outdoor = r.outdoor_kind; else delete as.outdoor;
    const altele = { ...(orig?.props ?? {}) } as Record<string, unknown>;
    if (Object.keys(as).length) altele["architecture.space"] = as;
    else delete altele["architecture.space"];
    obiecte.push({
      ...(orig ?? { id, type: "Space", source: SURSA }),
      id,
      type: "Space",
      properties: props,
      geometry: geo,
      ...(Object.keys(display).length ? { display } : {}),
      ...(Object.keys(altele).length ? { props: altele } : {}),
    } as CanonicalObject);
  }
  for (const id of camInit.keys()) if (!camVazute.has(id)) sterge.push(id);

  // ---- nucleul
  if (n.core && plan.core_rect && !acelasi(init.core_rect, plan.core_rect)) {
    const o = n.obiecte.get(n.core)!;
    const [u0, v0, u1, v1] = plan.core_rect;
    obiecte.push({ ...o, geometry: { ...(o.geometry ?? {}),
      poly: [[u0, v0], [u1, v0], [u1, v1], [u0, v1]].map(din) } });
  }
  const partiInit = new Map((init.core_parts ?? []).map((p) => [p.eid, p]));
  const partiVazute = new Set<string>();
  for (const p of plan.core_parts ?? []) {
    if (!p.eid || !partiInit.has(p.eid)) continue;
    partiVazute.add(p.eid);
    if (acelasi(partiInit.get(p.eid), p)) continue;
    const o = n.obiecte.get(p.eid)!;
    const geo: Record<string, unknown> = { ...(o.geometry ?? {}), poly: p.poly.map(din) };
    for (const k of ["cx", "cy", "w", "h", "deg"]) delete geo[k];
    obiecte.push({ ...o, properties: { ...o.properties, kind: p.kind }, geometry: geo });
  }
  for (const id of partiInit.keys()) if (id && !partiVazute.has(id)) sterge.push(id);

  // ---- nivelul: conturul și amenajarea
  const amenajare = (() => {
    const piese = plan.furniture ?? [];
    const aparate = plan.electrical ?? [];
    const fin = (plan.finishes ?? [])
      .filter((f) => idCamera[f.room])
      .map((f) => ({ space: idCamera[f.room],
                     ...(f.floor ? { floor: f.floor } : {}),
                     ...(f.wall ? { wall: f.wall } : {}) }));
    if (!piese.length && !aparate.length && !fin.length) return null;
    return {
      piese: piese.map(({ x: _x, y: _y, rot_deg: _r, ...rest }) => rest),
      piese_c: piese.map((p) => din([p.x.a, p.y.a])),
      piese_dir: piese.map((p) => dirDinUnghi(degDin(c, p.rot_deg ?? 0))),
      aparate: aparate.map(({ x: _x, y: _y, rot_deg: _r, ...rest }) => rest),
      aparate_c: aparate.map((p) => din([p.x.a, p.y.a])),
      aparate_dir: aparate.map((p) => dirDinUnghi(degDin(c, p.rot_deg ?? 0))),
      finisaje: fin,
    };
  })();
  const amenajareSchimbata = !acelasi(init.furniture ?? [], plan.furniture ?? [])
    || !acelasi(init.electrical ?? [], plan.electrical ?? [])
    || !acelasi(init.finishes ?? [], plan.finishes ?? [])
    // o cameră nouă poartă un finisaj pe un id care abia s-a născut
    || ((plan.finishes ?? []).length > 0
        && obiecte.some((o) => o.type === "Space"));
  const conturSchimbat = !acelasi(init.outline, plan.outline);
  if (conturSchimbat || amenajareSchimbata) {
    const props = { ...(st.props ?? {}) } as Record<string, unknown>;
    if (amenajare) props[AMENAJARE] = amenajare; else delete props[AMENAJARE];
    obiecte.push({
      ...st,
      geometry: conturSchimbat && plan.outline
        ? { ...(st.geometry ?? {}), poly: plan.outline.map(din) } : st.geometry,
      ...(Object.keys(props).length ? { props } : {}),
    } as CanonicalObject);
  }

  return { obiecte, sterge };
}

/** Numerele unui obiect gata de scris, curățate după drumul întors prin cadre:
 *  coordonatele la zecimea de milimetru, unghiurile în [0, 360). Rotirea, originea și
 *  oglindirea lasă în urmă `24.677999999999997` și `-359.99`, iar un document
 *  în care un zid neatins de nimeni ar sta la 24.678 și unul scris din editor
 *  la 24.677999999999997 nu mai poate fi comparat cu ochiul. */
export function curatNumerele(o: CanonicalObject): CanonicalObject {
  const pct = (v: unknown): unknown =>
    typeof v === "number" ? r4(v) : Array.isArray(v) ? v.map(pct) : v;
  const geo = o.geometry ? { ...o.geometry } : undefined;
  if (geo) {
    for (const k of ["poly", "holes", "axis", "mouth", "cx", "cy", "u_m"]) {
      if (geo[k] != null) geo[k] = pct(geo[k]);
    }
    if (typeof geo.deg === "number") geo.deg = unghi(geo.deg);
  }
  let props = o.props;
  const am = props?.[AMENAJARE] as Record<string, unknown> | undefined;
  if (am) {
    const curat = { ...am };
    for (const k of ["piese_c", "aparate_c"]) if (curat[k]) curat[k] = pct(curat[k]);
    const dir = (v: unknown): unknown => typeof v === "number"
      ? Math.round(v * 1e6) / 1e6 : Array.isArray(v) ? v.map(dir) : v;
    for (const k of ["piese_dir", "aparate_dir"]) if (curat[k]) curat[k] = dir(curat[k]);
    props = { ...props, [AMENAJARE]: curat };
  }
  return { ...o, ...(geo ? { geometry: geo } : {}), ...(props ? { props } : {}) };
}

/** Obiectele scrise în cadrul DESENULUI, duse în cel al DOCUMENTULUI: originea
 *  locală adăugată la loc, apoi drumul întors al lui `northUp` — aceleași chei
 *  de puncte și direcții ca la venire —, apoi numerele curățate. */
export function inCadrulDocumentului(
  obiecte: readonly CanonicalObject[], crs: CanonicalDoc["crs"],
  origine: readonly [number, number],
): CanonicalObject[] {
  const laOrigine = laOrigineLocala(
    { objects: [...obiecte] } as CanonicalDoc, [-origine[0], -origine[1]]);
  return northDown({ crs, objects: laOrigine.objects }).objects.map(curatNumerele);
}

// ------------------------------------------------------------ propagarea ---

/** Cât de aproape trebuie să fie două vârfuri ca să fie «același». */
const TOL = 0.01;

const aproape = (a: readonly number[], b: readonly number[], tol = TOL) =>
  Math.abs(a[0] - b[0]) <= tol && Math.abs(a[1] - b[1]) <= tol;

/** Aceleași vârfuri, în orice ordine și de oriunde începe inelul. */
function acelasiInel(a: readonly Pt[], b: readonly Pt[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((p) => b.some((q) => aproape(p, q)))
      && b.every((p) => a.some((q) => aproape(p, q)));
}

const inelP = (poly: readonly VarfAfin[]) => inelVal(poly);

const zidLaFel = (a: PereteDoc, b: PereteDoc) =>
  a.kind === b.kind && Math.abs(a.thickness_m - b.thickness_m) < 0.005
  && acelasiInel(inelP(a.poly), inelP(b.poly));
const golLaFel = (a: GolDoc, b: GolDoc) =>
  aproape([a.x.a, a.y.a], [b.x.a, b.y.a]) && Math.abs((a.w_m ?? 0.9) - (b.w_m ?? 0.9)) < TOL;
const camLaFel = (a: CameraDoc, b: CameraDoc) => acelasiInel(inelP(a.poly), inelP(b.poly));
const piesaLaFel = (a: { kind: string; x: { a: number }; y: { a: number };
                         h_m?: number; devices?: string[] },
                    b: { kind: string; x: { a: number }; y: { a: number };
                         h_m?: number; devices?: string[] }) =>
  a.kind === b.kind && aproape([a.x.a, a.y.a], [b.x.a, b.y.a])
  // Two mounting boxes may share a spot on purpose (a socket box under a
  // switch box), so place alone cannot say which is which: the height and the
  // devices tell them apart, or an edit to one would take out both.
  && (a.kind !== "doza"
      || (acelasi(a.h_m, b.h_m) && acelasi(a.devices ?? [], b.devices ?? [])));

function distLaSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2));
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}
function distLaInel(p: Pt, r: readonly Pt[]): number {
  let d = Infinity;
  for (let i = 0; i < r.length; i++) d = Math.min(d, distLaSegment(p, r[i], r[(i + 1) % r.length]));
  return d;
}

/** Cât din nivelul `a` se regăsește, la centimetru, pe nivelul `b`: pereți și
 *  goluri, ca fracție din ale lui `a`. Din cifra asta se hotărăște dacă o
 *  schimbare se propagă implicit pe `b`. */
export function parteaComuna(a: TronsonDoc, b: TronsonDoc): number {
  const wa = a.plan?.walls ?? [], wb = b.plan?.walls ?? [];
  const ga = [...(a.plan?.doors ?? []), ...(a.plan?.windows ?? [])];
  const gb = [...(b.plan?.doors ?? []), ...(b.plan?.windows ?? [])];
  const tot = wa.length + ga.length;
  if (!tot) return 0;
  let n = 0;
  for (const w of wa) if (wb.some((x) => zidLaFel(w, x))) n++;
  for (const x of ga) if (gb.some((y) => golLaFel(x, y))) n++;
  return n / tot;
}

/** DUCE PE NIVELUL `m0` CE S-A SCHIMBAT PE NIVEL DE LA `l0` LA `l1`.
 *
 *  Numai unde `m0` e la fel ca `l0`:
 *
 *    * un obiect ȘTERS sau SCHIMBAT pe `l` se șterge sau se schimbă pe `m`
 *      dacă `m` are unul identic cu cel de dinainte (la 1 cm);
 *    * un obiect NOU pe `l` se naște pe `m` dacă stă în PORȚIUNEA COMUNĂ: în
 *      camerele lui `l0` care se regăsesc pe `m`, sau lângă pereții care se
 *      regăsesc (5 cm). Un zid nou cere asta la ambele capete și la mijloc —
 *      pe etajul retras, un zid tras prin terasa pe care etajul n-o mai are
 *      nu se naște;
 *    * camerele lui `m` NU se copiază: se refac din pereții lui, cu aceeași
 *      derivare pe care o face masa după un gest (`cuCamereDerivate` peste
 *      `conturulDupaDesen`). Se copiază doar ce nu se vede din formă — felul,
 *      numele, balconul sau logia — pe camera identică.
 *
 *  Întoarce `null` când pe `m` n-a fost nimic de făcut. */
export function propaga(l0: TronsonDoc, l1: TronsonDoc, m0: TronsonDoc): TronsonDoc | null {
  const L0 = l0.plan!, L1 = l1.plan!, M0 = m0.plan!;
  let schimbat = false;

  // Porțiunea comună: camerele și pereții lui `l0` care se regăsesc pe `m0`.
  const camComune = (L0.rooms ?? []).filter((r) => (M0.rooms ?? []).some((x) => camLaFel(r, x)))
    .map((r) => inelP(r.poly));
  const zidComune = (L0.walls ?? []).filter((w) => (M0.walls ?? []).some((x) => zidLaFel(w, x)))
    .map((w) => inelP(w.poly));
  const inComun = (p: Pt) => camComune.some((r) => inInel(p, r))
    || zidComune.some((r) => distLaInel(p, r) <= 0.05 || inInel(p, r));

  // ---- pereții
  const walls = [...(M0.walls ?? [])];
  const idPeM = new Map<string, string>();       // id pe l → id pe m
  const l1Ziduri = new Map((L1.walls ?? []).map((w) => [w.id, w]));
  const l0Ids = new Set((L0.walls ?? []).map((w) => w.id));
  for (const w0 of L0.walls ?? []) {
    const j = walls.findIndex((x) => zidLaFel(w0, x));
    if (j < 0) continue;
    idPeM.set(w0.id, walls[j].id);
    const w1 = l1Ziduri.get(w0.id);
    if (!w1) { walls.splice(j, 1); schimbat = true; continue; }
    if (acelasi(w0, w1)) continue;
    walls[j] = { ...w1, id: walls[j].id };
    schimbat = true;
  }
  const idsM = new Set(walls.map((w) => w.id));
  for (const w1 of L1.walls ?? []) {
    if (l0Ids.has(w1.id)) continue;
    const [a, b] = axaPeretelui(w1);
    if (![a, b, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as Pt].every(inComun)) continue;
    let id = w1.id;
    let k = 1;
    while (idsM.has(id)) id = `${w1.id}.${k++}`;
    idsM.add(id);
    idPeM.set(w1.id, id);
    walls.push({ ...w1, id });
    schimbat = true;
  }

  // ---- golurile
  const goluri = (fel: "doors" | "windows") => {
    const out = [...(M0[fel] ?? [])];
    const l1Lista = L1[fel] ?? [];
    const l0Lista = L0[fel] ?? [];
    const cheie = (x: GolDoc) => x.eid;
    for (const g0 of l0Lista) {
      const j = out.findIndex((x) => golLaFel(g0, x));
      if (j < 0) continue;
      const g1 = g0.eid ? l1Lista.find((x) => cheie(x) === g0.eid) : undefined;
      if (!g1) { out.splice(j, 1); schimbat = true; continue; }
      const gazda = g1.wall ? idPeM.get(g1.wall) : null;
      if (acelasi(g0, g1) && (!g1.wall || gazda === out[j].wall
                              || gazda === undefined)) continue;
      if (g1.wall && gazda === undefined) { out.splice(j, 1); schimbat = true; continue; }
      out[j] = { ...g1, eid: out[j].eid, wall: gazda ?? null };
      schimbat = true;
    }
    const l0Eid = new Set(l0Lista.map((x) => x.eid).filter(Boolean));
    for (const g1 of l1Lista) {
      if (g1.eid && l0Eid.has(g1.eid)) continue;
      if (!inComun([g1.x.a, g1.y.a])) continue;
      const gazda = g1.wall ? idPeM.get(g1.wall) : null;
      if (g1.wall && gazda === undefined) continue;
      const { eid: _e, ...rest } = g1;
      out.push({ ...rest, wall: gazda ?? null });
      schimbat = true;
    }
    return out;
  };
  const doors = goluri("doors");
  const windows = goluri("windows");

  // ---- ce nu se vede din formă, pe camera identică; camerele unite pe `l`
  //      (și dispărute din listă) pleacă și de pe `m`
  let rooms = [...(M0.rooms ?? [])];
  const l1Cam = new Map((L1.rooms ?? []).filter((r) => r.eid).map((r) => [r.eid!, r]));
  const deScos = new Set<number>();
  // O cameră dispărută pe `l` pleacă și de pe `m` NUMAI dacă pe `m` s-au dus
  // și pereții: pe `l` a dispărut fiindcă pereții ei s-au schimbat (unire,
  // zid scos), iar pe un nivel unde pereții ăia n-au corespondent, camera nu
  // are de ce să plece. Măsurat pe P+4: fără condiția asta, un zid mutat pe
  // etajul 1 ștergea două camere de la parter, unde zidul nici nu există.
  const peretiDusi = schimbat;
  for (const r0 of L0.rooms ?? []) {
    const j = rooms.findIndex((x) => camLaFel(r0, x));
    if (j < 0 || !r0.eid) continue;
    const r1 = l1Cam.get(r0.eid);
    if (!r1) {
      if (peretiDusi) { deScos.add(j); schimbat = true; }
      continue;
    }
    if (r1.kind !== r0.kind || r1.label !== r0.label || r1.outdoor_kind !== r0.outdoor_kind) {
      const { label: _l, outdoor_kind: _o, ...fara } = rooms[j];
      rooms[j] = { ...fara, kind: r1.kind,
                   ...(r1.label ? { label: r1.label } : {}),
                   ...(r1.outdoor_kind ? { outdoor_kind: r1.outdoor_kind } : {}) };
      schimbat = true;
    }
  }
  if (deScos.size) rooms = rooms.filter((_, i) => !deScos.has(i));

  // ---- mobila și aparatele: fără id, deci pe loc și fel
  const lucruri = <T extends { kind: string; x: { a: number }; y: { a: number } }>(
    l0l: readonly T[], l1l: readonly T[], m0l: readonly T[]): T[] | null => {
    const scoase = l0l.filter((a) => !l1l.some((b) => acelasi(a, b)));
    const puse = l1l.filter((b) => !l0l.some((a) => acelasi(a, b)));
    if (!scoase.length && !puse.length) return null;
    const out = m0l.filter((m) => !scoase.some((a) => piesaLaFel(a, m)));
    for (const b of puse) if (inComun([b.x.a, b.y.a])) out.push(b);
    if (out.length !== m0l.length || puse.length) schimbat = true;
    return out;
  };
  const furniture = lucruri(L0.furniture ?? [], L1.furniture ?? [], M0.furniture ?? []);
  const electrical = lucruri(L0.electrical ?? [], L1.electrical ?? [], M0.electrical ?? []);

  // ---- conturul, când e al lor comun
  let outline = M0.outline;
  if (!acelasi(L0.outline, L1.outline) && L0.outline && M0.outline && L1.outline
      && acelasiInel(L0.outline, M0.outline)) {
    outline = L1.outline;
    schimbat = true;
  }

  // ---- nucleul, când e al lor comun
  let core_rect = M0.core_rect;
  let core_parts = M0.core_parts;
  if (L0.core_rect && M0.core_rect && L1.core_rect
      && !acelasi(L0.core_rect, L1.core_rect)
      && L0.core_rect.every((v, i) => Math.abs(v - M0.core_rect![i]) <= TOL)) {
    core_rect = L1.core_rect;
    core_parts = (L1.core_parts ?? []).map((p, i) => ({ ...p, eid: M0.core_parts?.[i]?.eid }));
    schimbat = true;
  }

  if (!schimbat) return null;
  let m1: TronsonDoc = {
    ...m0,
    plan: {
      ...M0, walls, doors, windows, rooms,
      ...(outline ? { outline } : {}),
      ...(furniture ? { furniture } : {}),
      ...(electrical ? { electrical: electrical.map((e) => {
        // `pentru` arată o piesă prin poziția ei în listă; o listă refăcută
        // pe alt nivel n-o mai are la același loc.
        const { pentru: _p, ...rest } = e as ElectricDoc;
        return rest as ElectricDoc;
      }) } : {}),
      ...(core_rect ? { core_rect, core_parts } : {}),
    },
  };
  m1 = cuCamereDerivate(conturulDupaDesen(m0, m1), reperulCamerelor(m0), m0);

  // ---- finisajele, pe camera identică — după derivare, pe indicii ei
  const finL = (d: TronsonDoc) => new Map((d.plan?.finishes ?? [])
    .map((f) => [d.plan!.rooms[f.room]?.eid, f] as const).filter(([e]) => e));
  const f0 = finL(l0), f1 = finL(l1);
  const schimbate = new Set([...f0.keys(), ...f1.keys()]
    .filter((e) => !acelasi({ ...f0.get(e), room: 0 }, { ...f1.get(e), room: 0 })));
  if (schimbate.size) {
    const finishes = [...(m1.plan!.finishes ?? [])];
    for (const e of schimbate) {
      const r0 = (L0.rooms ?? []).find((r) => r.eid === e);
      if (!r0) continue;
      const i = m1.plan!.rooms.findIndex((x) => camLaFel(r0, x));
      if (i < 0) continue;
      const k = finishes.findIndex((f) => f.room === i);
      const nou = f1.get(e);
      if (k >= 0) finishes.splice(k, 1);
      if (nou) finishes.push({ ...nou, room: i });
    }
    m1 = { ...m1, plan: { ...m1.plan!, finishes } };
  }
  return m1;
}
